import { createContext, useCallback, useEffect, useRef, useState } from "react";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "/v2/api";
const AUTH_BASE = `${API_BASE}/auth`;
const REFRESH_KEY = "qf_refresh_token";

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  // Track whether the initial session restore attempt has completed
  const [ready, setReady] = useState(false);
  // Ref so authFetch can always read the latest token without a stale closure
  const accessTokenRef = useRef(null);

  const _storeTokens = useCallback((access, refresh) => {
    setAccessToken(access);
    accessTokenRef.current = access;
    if (refresh) {
      try {
        localStorage.setItem(REFRESH_KEY, refresh);
      } catch (_) {}
    }
  }, []);

  const _clearSession = useCallback(() => {
    setUser(null);
    setAccessToken(null);
    accessTokenRef.current = null;
    try {
      localStorage.removeItem(REFRESH_KEY);
    } catch (_) {}
  }, []);

  // Attempt to silently refresh on mount using a stored refresh token
  useEffect(() => {
    const storedRefresh = (() => {
      try { return localStorage.getItem(REFRESH_KEY); } catch (_) { return null; }
    })();

    if (!storedRefresh) {
      setReady(true);
      return;
    }

    fetch(`${AUTH_BASE}/refresh/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh: storedRefresh }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => {
        _storeTokens(data.access, data.refresh);
        return fetch(`${AUTH_BASE}/me/`, {
          headers: { Authorization: `Bearer ${data.access}` },
        });
      })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((me) => setUser(me))
      .catch(_clearSession)
      .finally(() => setReady(true));
  }, [_storeTokens, _clearSession]);

  // Expose a refresh function so authFetch can call it on 401
  const refresh = useCallback(async () => {
    const storedRefresh = (() => {
      try { return localStorage.getItem(REFRESH_KEY); } catch (_) { return null; }
    })();
    if (!storedRefresh) { _clearSession(); return null; }

    const r = await fetch(`${AUTH_BASE}/refresh/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh: storedRefresh }),
    });
    if (!r.ok) { _clearSession(); return null; }
    const data = await r.json();
    _storeTokens(data.access, data.refresh);
    return data.access;
  }, [_storeTokens, _clearSession]);

  const login = useCallback(async (username, password) => {
    const r = await fetch(`${AUTH_BASE}/login/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.detail || "Invalid credentials.");
    }
    const data = await r.json();
    _storeTokens(data.access, data.refresh);
    setUser(data.user);
    return data.user;
  }, [_storeTokens]);

  const register = useCallback(async (fields) => {
    const r = await fetch(`${AUTH_BASE}/register/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(fields),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      // Surface first validation error
      const first = Object.values(err)[0];
      throw new Error(Array.isArray(first) ? first[0] : (err.detail || "Registration failed."));
    }
    const data = await r.json();
    _storeTokens(data.access, data.refresh);
    setUser(data.user);
    return data;
  }, [_storeTokens]);

  /**
   * googleAuth: POST Google credential to /auth/google/.
   * - Returns {user, access, refresh} on successful login (existing user).
   * - Returns {status: "onboarding", onboarding_token, email, suggested_name}
   *   for new users who still need to pick a username + country.
   */
  const googleAuth = useCallback(async (credential, pendingAttempts = []) => {
    const body = { credential };
    if (pendingAttempts.length > 0) {
      body.pending_attempts = pendingAttempts.map(({ quiz_slug, attempt_token }) => ({
        quiz_slug,
        attempt_token,
      }));
    }
    const r = await fetch(`${AUTH_BASE}/google/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.detail || "Google sign-in failed.");
    }
    const data = await r.json();
    if (data.status === "onboarding") {
      return data; // caller handles onboarding redirect
    }
    _storeTokens(data.access, data.refresh);
    setUser(data.user);
    return data;
  }, [_storeTokens]);

  /**
   * googleComplete: POST onboarding_token + username + country/state to
   * /auth/google/complete/ to finish new-user registration.
   */
  const googleComplete = useCallback(async (fields) => {
    const r = await fetch(`${AUTH_BASE}/google/complete/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(fields),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      const first = Object.values(err)[0];
      throw new Error(Array.isArray(first) ? first[0] : (err.detail || "Sign-up failed."));
    }
    const data = await r.json();
    _storeTokens(data.access, data.refresh);
    setUser(data.user);
    return data;
  }, [_storeTokens]);

  const logout = useCallback(async () => {
    const storedRefresh = (() => {
      try { return localStorage.getItem(REFRESH_KEY); } catch (_) { return null; }
    })();
    const token = accessTokenRef.current;
    _clearSession();
    // Best-effort blacklist — don't block UI on it
    if (storedRefresh && token) {
      fetch(`${AUTH_BASE}/logout/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ refresh: storedRefresh }),
        keepalive: true,
      }).catch(() => {});
    }
    // Full page reload so all React state is reset and the browser re-fetches
    // the server-rendered HTML with correct meta tags for the home page.
    window.location.replace("/v2/");
  }, [_clearSession]);

  // authFetch: fetch wrapper that injects Bearer and retries once on 401.
  // Also proactively refreshes when the access token is within 60s of expiry
  // so endpoints using OptionalJWTAuthentication (which swallow expired tokens
  // and return 200 anonymous instead of 401) never silently downgrade the request.
  const authFetch = useCallback(async (url, options = {}) => {
    let token = accessTokenRef.current;

    if (token) {
      try {
        const exp = JSON.parse(atob(token.split(".")[1])).exp;
        if (exp * 1000 - Date.now() < 60_000) {
          token = (await refresh()) ?? token;
        }
      } catch (_) {
        // malformed token — proceed with what we have
      }
    }

    const headers = { ...(options.headers || {}) };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    let response = await fetch(url, { ...options, headers });

    if (response.status === 401 && token) {
      const newToken = await refresh();
      if (newToken) {
        headers["Authorization"] = `Bearer ${newToken}`;
        response = await fetch(url, { ...options, headers });
      }
    }

    return response;
  }, [refresh]);

  // idGuard: if supplied, the update is a no-op if the current user's id
  // no longer matches — prevents a delayed score response from stamping one
  // user's points onto a different account that logged in afterwards.
  const updateUser = useCallback((partial, idGuard) => {
    setUser((prev) => {
      if (!prev) return prev;
      if (idGuard != null && prev.id !== idGuard) return prev;
      return { ...prev, ...partial };
    });
  }, []);

  return (
    <AuthContext.Provider value={{ user, accessToken, ready, login, logout, register, googleAuth, googleComplete, authFetch, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};
